import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { customUserDataFieldService } from '../../services/customUserDataFieldService';

interface SignaturePreviewProps {
    /** The signature field value: the stored UserSignature id, or empty if not set. */
    value?: string;
    /** Inline base64 data-URI thumbnail provided by the API. Avoids a network fetch when present. */
    thumbnailBase64?: string | null;
    /** Maximum rendered image height in pixels. */
    maxHeight?: number;
}

/**
 * Renders a stored signature image for a Signature-type data field.
 * Prefers the inline thumbnail from the DTO; falls back to an authenticated
 * fetch when no thumbnail is provided.
 */
const SignaturePreview: React.FC<SignaturePreviewProps> = ({ value, thumbnailBase64, maxHeight = 64 }) => {
    const [objectUrl, setObjectUrl] = useState<string | null>(null);
    const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');

    const hasThumbnail = !!thumbnailBase64;

    useEffect(() => {
        if (hasThumbnail) return;

        const signatureId = value?.trim();
        if (!signatureId) {
            setObjectUrl(null);
            setStatus('idle');
            return;
        }

        let cancelled = false;
        let createdUrl: string | null = null;
        setStatus('loading');

        customUserDataFieldService.getSignatureObjectUrl(signatureId)
            .then((url) => {
                if (cancelled) {
                    URL.revokeObjectURL(url);
                    return;
                }
                createdUrl = url;
                setObjectUrl(url);
                setStatus('idle');
            })
            .catch((err) => {
                console.error('Error loading signature image:', err);
                if (!cancelled) {
                    setStatus('error');
                }
            });

        return () => {
            cancelled = true;
            if (createdUrl) {
                URL.revokeObjectURL(createdUrl);
            }
        };
    }, [value, hasThumbnail]);

    if (!value?.trim()) {
        return (
            <Typography variant="body1" color="text.secondary">
                Not set
            </Typography>
        );
    }

    const imgSrc = hasThumbnail ? thumbnailBase64! : objectUrl;

    if (!hasThumbnail && status === 'loading') {
        return <CircularProgress size={20} />;
    }

    if (!imgSrc) {
        return (
            <Typography variant="body2" color="error">
                Unable to load signature
            </Typography>
        );
    }

    return (
        <Box
            component="img"
            src={imgSrc}
            alt="Signature"
            sx={{
                maxHeight,
                maxWidth: '100%',
                objectFit: 'contain',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 0.5,
                backgroundColor: 'background.paper',
            }}
        />
    );
};

export default SignaturePreview;
